import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { toEmbedUrl } from "@/lib/video";
import { detectDuration, fmtDuration } from "@/lib/videoDuration";
import { UploadField } from "@/components/UploadField";
import { RichText } from "@/components/RichText";
import { VideoUpload } from "@/components/VideoUpload";
import { VideoPicker } from "@/pages/admin/VideoLibrary";
import {
  ArrowLeft, Loader2, Save, PlaySquare, Film, FileText, Code2, Type, Headphones, Radio, Plus, X, CheckCircle2,
} from "lucide-react";

const errMsg = (e) => formatApiError(e.response?.data?.detail);

export const CONTENT_TYPES = [
  { v: "video", icon: PlaySquare, title: "Vídeo", desc: "Link do YouTube/Vimeo ou vídeo enviado para a própria plataforma. Ideal para as aulas gravadas." },
  { v: "file", icon: FileText, title: "Arquivo", desc: "PDFs (com marca d'água do aluno), apostilas, planilhas e outros materiais." },
  { v: "embed", icon: Code2, title: "Embed", desc: "Incorpore conteúdo externo (Canva, Google Slides, Forms…) pelo código embed." },
  { v: "text", icon: Type, title: "Editor de texto", desc: "Conteúdo com texto formatado, listas, links e imagens." },
  { v: "audio", icon: Headphones, title: "Podcast / Áudio", desc: "Aula só em áudio, ótima para revisões e explicações rápidas." },
  { v: "live", icon: Radio, title: "Ao vivo / Reunião", desc: "Transmissão ou encontro (YouTube Live, Zoom, Meet) com data e hora." },
];

const EMPTY = {
  title: "", description: "", duration: "", published: false, notify: false, professor_id: "", content_type: "video",
  video_url: "", video_source: "link", video_file: {}, embed_url: "", html: "", audio_url: "", live_url: "", live_at: "", pdfs: [],
  show_watermark: true, is_requirement: false, require_stay: false, stay_mode: "percent", stay_value: 75,
  release_mode: "now", release_at: "", release_days: 0, close_mode: "never", close_at: "", close_days: 0,
  view_mode: "no_skip", max_pauses: 0,
};

const Opt = ({ label, hint, children }) => (
  <div className="grid md:grid-cols-[300px_1fr] gap-2 md:gap-6 py-3 border-b border-slate-100 last:border-0 items-start">
    <div><Label className="text-slate-800">{label}</Label>{hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}</div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);
const Sel2 = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[200px]">{children}</select>
);

const Seg = ({ value, onChange, options, disabled }) => (
  <div className={`inline-grid grid-cols-2 rounded-md overflow-hidden border border-slate-200 w-72 ${disabled ? "opacity-50" : ""}`}>
    {options.map(([v, l]) => (
      <button key={String(v)} type="button" disabled={disabled} onClick={() => onChange(v)}
        className={`h-10 text-sm font-medium ${value === v ? "bg-[#232D4F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
    ))}
  </div>
);

export default function LessonEditor() {
  const { id: courseId, lid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [moduleId, setModuleId] = useState(params.get("m") || "");
  const [f, setF] = useState(null);
  const [pdfDraft, setPdfDraft] = useState({ name: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [videoInfo, setVideoInfo] = useState({ state: "", text: "" }); // leitura automática do vídeo
  const [hostedUrl, setHostedUrl] = useState("");
  const [picking, setPicking] = useState(false);
  const back = () => navigate(`/admin/cursos/${courseId}#aulas${moduleId ? `?m=${moduleId}` : ""}`);

  useEffect(() => {
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});
    api.get(`/courses/${courseId}`).then((r) => {
      const c = r.data; setCourse(c);
      if (lid) {
        for (const m of c.modules) {
          const l = m.lessons.find((x) => x.id === lid);
          if (l) { setModuleId(m.id); setF({ ...EMPTY, ...l, published: l.published !== false, notify: false, content_type: l.content_type || (l.video_url ? "video" : l.pdfs?.length ? "file" : "video") }); return; }
        }
        toast.error("Aula não encontrada."); navigate(`/admin/cursos/${courseId}#aulas`);
      } else {
        const wanted = params.get("m");
        setModuleId(c.modules.some((m) => m.id === wanted) ? wanted : (c.modules[0]?.id || ""));
        setF({ ...EMPTY, professor_id: c.coordinator_id || "" });
      }
    }).catch(() => navigate("/admin/cursos"));
  }, [courseId, lid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ao colar o link do vídeo: preenche a duração (e o título, se vazio) automaticamente
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const url = f?.video_url?.trim();
    if (!f || f.content_type !== "video" || f.video_source === "upload" || !url || !toEmbedUrl(url)) { setVideoInfo({ state: "", text: "" }); return; }
    let alive = true;
    const t = setTimeout(async () => {
      setVideoInfo({ state: "loading", text: "Lendo informações do vídeo…" });
      let title = "", dur = "";
      try {
        const r = await api.get("/admin/video-info", { params: { url } });
        title = r.data.title || ""; dur = r.data.duration || "";
      } catch (e) { /* segue para o navegador */ }
      if (!dur) { const sec = await detectDuration(url); if (sec) dur = fmtDuration(sec); }
      if (!alive) return;
      setF((p) => ({ ...p, duration: dur || p.duration, title: p.title?.trim() ? p.title : (title || p.title) }));
      setVideoInfo(dur ? { state: "ok", text: `Duração detectada automaticamente: ${dur}${title ? ` · ${title}` : ""}` }
        : { state: "warn", text: "Não foi possível ler a duração deste vídeo (privado ou restrito). Preencha manualmente." });
    }, 700);
    return () => { alive = false; clearTimeout(t); };
  }, [f?.video_url, f?.content_type, f?.video_source]); // eslint-disable-line react-hooks/exhaustive-deps

  // prévia do vídeo enviado (link temporário)
  const hostedId = f?.content_type === "video" && f?.video_source === "upload" ? f?.video_file?.id : "";
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setHostedUrl("");
    if (!hostedId) return;
    api.get(`/admin/videos/${hostedId}/preview`).then((r) => setHostedUrl(r.data.url)).catch(() => setHostedUrl(""));
  }, [hostedId]);

  if (!course || !f) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const type = CONTENT_TYPES.find((t) => t.v === f.content_type);
  const embedPreview = f.content_type === "video" ? toEmbedUrl(f.video_url) : null;
  const embedSrc = (() => { const m = (f.embed_url || "").match(/src\s*=\s*["']([^"']+)["']/i); const u = (m ? m[1] : f.embed_url).trim(); return u.startsWith("https://") ? u : ""; })();

  const pickVideo = (v) => setF((p) => ({
    ...p, video_file: { id: v.id, name: v.name, size: v.size },
    duration: v.duration ? fmtDuration(v.duration) : p.duration,
    title: p.title?.trim() ? p.title : v.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "),
  }));

  const newModule = async () => {
    const title = window.prompt("Nome do novo módulo:");
    if (!title?.trim()) return;
    try {
      const r = await api.post(`/courses/${courseId}/modules`, { title: title.trim(), published: true, professor_id: f.professor_id });
      setCourse(r.data); setModuleId(r.data.modules[r.data.modules.length - 1].id); toast.success("Módulo criado.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addPdf = () => {
    if (!pdfDraft.name.trim() || !pdfDraft.url.trim()) return;
    set("pdfs", [...f.pdfs, pdfDraft]); setPdfDraft({ name: "", url: "" });
  };

  const save = async () => {
    if (!f.title.trim()) return toast.error("Defina um título para a aula.");
    if (!moduleId) return toast.error("Selecione o módulo (ou crie um).");
    const t = f.content_type;
    if (t === "video" && f.video_source === "upload" && !f.video_file?.id) return toast.error("Envie o vídeo da aula (ou escolha a opção de link).");
    if (t === "video" && f.video_source !== "upload" && f.video_url && !embedPreview) return toast.error("Link de vídeo não reconhecido (use YouTube ou Vimeo).");
    if (t === "embed" && !embedSrc) return toast.error("Cole o código embed ou um link https.");
    if (t === "live" && !f.live_url.trim()) return toast.error("Informe o link da transmissão/reunião.");
    if (t === "audio" && !f.audio_url.trim()) return toast.error("Envie o áudio ou informe o link.");
    if (t === "file" && f.pdfs.length === 0) return toast.error("Adicione ao menos um arquivo.");
    setSaving(true);
    try {
      const body = { ...f, notify: f.published && f.notify };
      delete body.id;
      if (lid) await api.put(`/courses/${courseId}/modules/${moduleId}/lessons/${lid}`, body);
      else await api.post(`/courses/${courseId}/modules/${moduleId}/lessons`, body);
      toast.success(lid ? "Aula atualizada." : "Aula criada." + (body.notify ? " Alunos avisados." : ""));
      back();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-5xl">
      <div className="flex items-center gap-3">
        <button onClick={back} className="text-slate-400 hover:text-slate-800"><ArrowLeft className="h-5 w-5" /></button>
        <div>
          <div className="text-xs text-slate-400">{course.title}</div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{lid ? "Editar aula" : "Nova aula"}</h1>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5">
        <div className="flex flex-wrap gap-8">
          <div className="space-y-1.5"><Label><span className="text-red-500 mr-1">*</span>Status</Label>
            <Seg value={f.published} onChange={(v) => { set("published", v); if (!v) set("notify", false); }} options={[[false, "Rascunho"], [true, "Publicado"]]} /></div>
          <div className="space-y-1.5"><Label>Notificar alunos?</Label>
            <Seg value={f.notify} onChange={(v) => set("notify", v)} options={[[false, "Não"], [true, "Sim"]]} disabled={!f.published} />
            <p className="text-xs text-slate-400">{f.published ? "Aviso no sino da plataforma para os alunos matriculados." : "Disponível ao publicar."}</p></div>
        </div>

        <div className="space-y-1.5"><Label><span className="text-red-500 mr-1">*</span>Título</Label>
          <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Defina um título para a aula" data-testid="lesson-title-input" /></div>

        <div className="grid md:grid-cols-[1fr_1fr_140px] gap-4">
          <div className="space-y-1.5"><Label>Professor</Label>
            <select value={f.professor_id} onChange={(e) => set("professor_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">Selecione um professor</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select></div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between"><Label><span className="text-red-500 mr-1">*</span>Módulo</Label>
              <button type="button" onClick={newModule} className="text-xs text-[#1E9E2A] font-semibold inline-flex items-center gap-1 hover:underline"><Plus className="h-3.5 w-3.5" /> Novo módulo</button></div>
            <select value={moduleId} onChange={(e) => setModuleId(e.target.value)} disabled={!!lid} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm disabled:opacity-60" data-testid="lesson-module-select">
              {course.modules.length === 0 && <option value="">Nenhum módulo — crie em “+ Novo módulo”</option>}
              {course.modules.map((m, i) => <option key={m.id} value={m.id}>{i + 1}. {m.title}{m.published === false ? " (rascunho)" : ""}</option>)}
            </select>
            {lid && <p className="text-[11px] text-slate-400">Para trocar de módulo use “Mover para outro módulo” na lista de aulas.</p>}
          </div>
          <div className="space-y-1.5"><Label>Duração</Label><Input value={f.duration} onChange={(e) => set("duration", e.target.value)} placeholder="10:00" /></div>
        </div>

        <div className="space-y-1.5"><Label>Descrição</Label>
          <Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Escreva algum complemento sobre essa aula" /></div>
      </div>

      {/* Modelo do conteúdo */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5">
        <div>
          <Label className="text-base"><span className="text-red-500 mr-1">*</span>Modelo do conteúdo</Label>
          <div className="mt-2 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50/60 p-3 w-fit pr-6">
            <span className="h-10 w-10 rounded-lg grid place-items-center bg-white text-[#1E9E2A]"><type.icon className="h-5 w-5" /></span>
            <div><div className="font-semibold text-slate-800">{type.title}</div><div className="text-xs text-slate-500">Modelo selecionado</div></div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="lesson-content-types">
          {CONTENT_TYPES.map((t) => {
            const on = t.v === f.content_type;
            return (
              <button key={t.v} type="button" onClick={() => set("content_type", t.v)} data-testid={`lesson-type-${t.v}`}
                className={`text-left flex gap-3 rounded-xl border p-4 transition-colors ${on ? "border-[#1E9E2A] bg-green-50/50 ring-1 ring-[#1E9E2A]" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className={`h-10 w-10 shrink-0 rounded-lg grid place-items-center ${on ? "bg-[#1E9E2A] text-white" : "bg-slate-100 text-slate-600"}`}><t.icon className="h-5 w-5" /></span>
                <span className="min-w-0">
                  <span className="font-semibold text-slate-800 flex items-center gap-1.5">{t.title}{on && <CheckCircle2 className="h-4 w-4 text-[#1E9E2A]" />}</span>
                  <span className="block text-sm text-slate-500 mt-0.5">{t.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="border-t border-slate-100 pt-5 space-y-4">
          {f.content_type === "video" && (
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="video-source">
              {[["link", "Link do YouTube / Vimeo"], ["upload", "Enviar vídeo para a plataforma"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => set("video_source", k)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${(f.video_source || "link") === k ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
          )}
          {f.content_type === "video" && f.video_source === "upload" && (
            <div className="space-y-3" data-testid="video-hosted">
              {f.video_file?.id ? (
                <>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-[#1E9E2A]" />
                    <span className="font-medium text-slate-800 truncate">{f.video_file.name}</span>
                    {f.video_file.size ? <span className="text-slate-400">{(f.video_file.size / 1024 ** 2).toFixed(0)} MB</span> : null}
                    <button type="button" onClick={() => set("video_file", {})} className="text-xs text-red-600 hover:underline">Trocar vídeo</button>
                  </div>
                  {hostedUrl && <video src={hostedUrl} controls controlsList="nodownload" disablePictureInPicture className="rounded-lg border border-slate-200 bg-black aspect-video w-full max-w-2xl" />}
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <VideoUpload onDone={pickVideo} />
                  <Button type="button" variant="outline" onClick={() => setPicking(true)} data-testid="video-pick-btn"><Film className="h-4 w-4 mr-2" /> Escolher da biblioteca</Button>
                </div>
              )}
              <VideoPicker open={picking} onClose={() => setPicking(false)} onPick={(v) => { setPicking(false); pickVideo(v); }} />
              <p className="text-xs text-slate-400 max-w-2xl">
                MP4 (H.264) até 6 GB. O vídeo fica protegido: só alunos com acesso à aula conseguem assistir, por um link temporário,
                com marca d'água e sem botão de download. Para aulas longas e muitos alunos, o Vimeo continua sendo mais leve para o servidor.
              </p>
            </div>
          )}
          {f.content_type === "video" && f.video_source !== "upload" && (
            <>
              <div className="space-y-1.5"><Label>Link do vídeo (YouTube / Vimeo)</Label>
                <Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://vimeo.com/..." data-testid="lesson-video-input" /></div>
              {videoInfo.text && (
                <p className={`text-xs ${videoInfo.state === "ok" ? "text-[#1E9E2A]" : videoInfo.state === "warn" ? "text-amber-700" : "text-slate-400"}`} data-testid="video-info">
                  {videoInfo.state === "loading" ? "⏳ " : videoInfo.state === "ok" ? "⏱ " : "⚠️ "}{videoInfo.text}
                </p>
              )}
              {f.video_url && (embedPreview
                ? <div className="rounded-lg overflow-hidden border border-slate-200 aspect-video max-w-2xl"><iframe src={embedPreview} title="prévia" className="w-full h-full" allow="encrypted-media; fullscreen; picture-in-picture" allowFullScreen /></div>
                : <p className="text-xs text-orange-600">Link não reconhecido. Use um link do YouTube ou Vimeo.</p>)}
            </>
          )}
          {f.content_type === "embed" && (
            <>
              <div className="space-y-1.5"><Label>Código embed ou link</Label>
                <Textarea rows={3} value={f.embed_url} onChange={(e) => set("embed_url", e.target.value)} placeholder={'<iframe src="https://..."></iframe>  ou  https://...'} className="font-mono text-xs" data-testid="lesson-embed-input" />
                <p className="text-xs text-slate-400">Por segurança, só o endereço https do iframe é usado.</p></div>
              {embedSrc && <div className="rounded-lg overflow-hidden border border-slate-200 aspect-video max-w-2xl"><iframe src={embedSrc} title="prévia" className="w-full h-full" sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation" allowFullScreen /></div>}
            </>
          )}
          {f.content_type === "text" && (
            <div className="space-y-1.5"><Label>Conteúdo da aula</Label><RichText value={f.html} onChange={(v) => set("html", v)} /></div>
          )}
          {f.content_type === "audio" && (
            <>
              <div className="space-y-1.5"><Label>Áudio (MP3, M4A, OGG, WAV)</Label>
                <div className="flex gap-2 flex-wrap">
                  <Input value={f.audio_url} onChange={(e) => set("audio_url", e.target.value)} placeholder="https://... ou envie o arquivo" className="flex-1 min-w-[240px]" />
                  <UploadField accept="audio/*,.mp3,.m4a,.ogg,.wav" label="Enviar áudio" onUploaded={(url) => set("audio_url", url)} />
                </div></div>
              {f.audio_url && <audio controls src={f.audio_url} className="w-full max-w-xl" />}
            </>
          )}
          {f.content_type === "live" && (
            <div className="grid md:grid-cols-[2fr_1fr] gap-4">
              <div className="space-y-1.5"><Label><span className="text-red-500 mr-1">*</span>Link da transmissão ou reunião</Label>
                <Input value={f.live_url} onChange={(e) => set("live_url", e.target.value)} placeholder="https://youtube.com/live/...  |  https://zoom.us/j/...  |  https://meet.google.com/..." data-testid="lesson-live-url" /></div>
              <div className="space-y-1.5"><Label>Data e hora</Label>
                <Input type="datetime-local" value={f.live_at} onChange={(e) => set("live_at", e.target.value)} /></div>
              <p className="text-xs text-slate-400 md:col-span-2">O aluno vê a data, a contagem regressiva e o botão “Entrar”. Depois do encontro, você pode trocar para “Vídeo externo” com a gravação.</p>
            </div>
          )}

          {/* arquivos: conteúdo principal no modelo Arquivo, complementares nos demais */}
          <div className="space-y-2 pt-2">
            <Label>{f.content_type === "file" ? "Arquivos da aula" : "Materiais complementares (opcional)"}</Label>
            {f.pdfs.map((p, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-[#E65100]" /><span className="flex-1 truncate">{p.name}</span>
                <button type="button" onClick={() => set("pdfs", f.pdfs.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <UploadField accept="" label="Enviar arquivo (ou arraste aqui)"
                onUploaded={(url, filename) => set("pdfs", [...f.pdfs, { name: filename || "Material", url }])} />
              <span className="text-xs text-slate-400">ou por link:</span>
              <Input className="w-44" value={pdfDraft.name} onChange={(e) => setPdfDraft((p) => ({ ...p, name: e.target.value }))} placeholder="Nome" />
              <Input className="flex-1 min-w-[200px]" value={pdfDraft.url} onChange={(e) => setPdfDraft((p) => ({ ...p, url: e.target.value }))} placeholder="URL" />
              <Button type="button" variant="outline" onClick={addPdf}><Plus className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-slate-400">PDFs são entregues ao aluno com nome, e-mail e data em todas as páginas (marca d'água).</p>
          </div>
        </div>
      </div>

      {/* Configurações da aula (só o admin vê; valem para o aluno) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid="lesson-settings">
        <h2 className="font-display text-lg font-semibold text-slate-900">Configurações da aula</h2>
        <p className="text-xs text-slate-400 mb-2">Visíveis só para o administrador. As regras são aplicadas na sala de aula do aluno.</p>
        <Opt label="Exibir identificação do aluno?" hint="Nome, CPF e IP sobre o vídeo (desencoraja gravar e repassar).">
          <Seg value={f.show_watermark} onChange={(v) => set("show_watermark", v)} options={[[true, "Sim"], [false, "Não"]]} />
        </Opt>
        <Opt label="Tornar aula requisito?" hint="As aulas seguintes só liberam depois que o aluno concluir esta.">
          <Seg value={f.is_requirement} onChange={(v) => set("is_requirement", v)} options={[[true, "Sim"], [false, "Não"]]} />
        </Opt>
        <Opt label="Obrigar permanência na aula?" hint="O aluno só consegue concluir depois de assistir o tempo mínimo (vídeo ou áudio).">
          <Seg value={f.require_stay} onChange={(v) => set("require_stay", v)} options={[[true, "Sim"], [false, "Não"]]} />
        </Opt>
        {f.require_stay && (
          <Opt label="Tempo de permanência na aula" hint={f.stay_mode === "percent" && !f.duration ? "⚠️ Preencha a Duração da aula para usar porcentagem." : ""}>
            <Sel2 value={f.stay_mode} onChange={(v) => { set("stay_mode", v); set("stay_value", v === "percent" ? 75 : 10); }} testid="lesson-stay-mode">
              <option value="percent">Porcentagem do vídeo</option>
              <option value="minutes">Minutos</option>
            </Sel2>
            {f.stay_mode === "percent" ? (
              <Sel2 value={String(f.stay_value)} onChange={(v) => set("stay_value", parseInt(v, 10))} testid="lesson-stay-value">
                {[50, 60, 70, 75, 80, 90, 100].map((n) => <option key={n} value={String(n)}>{n}% do vídeo</option>)}
              </Sel2>
            ) : (
              <><Input type="number" min="1" max="600" value={f.stay_value} onChange={(e) => set("stay_value", parseInt(e.target.value, 10) || 1)} className="w-24" /> <span className="text-sm text-slate-500">minutos</span></>
            )}
          </Opt>
        )}
        <Opt label="Liberar aula">
          <Sel2 value={f.release_mode} onChange={(v) => set("release_mode", v)} testid="lesson-release-mode">
            <option value="now">Imediatamente</option>
            <option value="date">Em uma data</option>
            <option value="after_enroll">Dias após a matrícula</option>
          </Sel2>
          {f.release_mode === "date" && <Input type="datetime-local" value={f.release_at} onChange={(e) => set("release_at", e.target.value)} className="w-56" />}
          {f.release_mode === "after_enroll" && <><Input type="number" min="0" value={f.release_days} onChange={(e) => set("release_days", parseInt(e.target.value, 10) || 0)} className="w-24" /><span className="text-sm text-slate-500">dias após a matrícula</span></>}
        </Opt>
        <Opt label="Período para encerramento do acesso">
          <Sel2 value={f.close_mode} onChange={(v) => set("close_mode", v)}>
            <option value="never">Não encerra</option>
            <option value="date">Em uma data</option>
            <option value="after_release">Dias após a liberação</option>
          </Sel2>
          {f.close_mode === "date" && <Input type="datetime-local" value={f.close_at} onChange={(e) => set("close_at", e.target.value)} className="w-56" />}
          {f.close_mode === "after_release" && <><Input type="number" min="1" value={f.close_days} onChange={(e) => set("close_days", parseInt(e.target.value, 10) || 0)} className="w-24" /><span className="text-sm text-slate-500">dias após liberar</span></>}
        </Opt>
        <Opt label="Visualização da aula" hint="“Sem avançar”: o aluno não consegue pular para frente do que já assistiu.">
          <Sel2 value={f.view_mode} onChange={(v) => set("view_mode", v)} testid="lesson-view-mode">
            <option value="free">Livre (pode avançar o vídeo)</option>
            <option value="no_skip">Sem avançar o vídeo</option>
          </Sel2>
        </Opt>
        <Opt label="Pausas durante a aula" hint="Quantas pausas o aluno pode fazer (0 = sem limite). Passando do limite, o vídeo volta a tocar.">
          <Input type="number" min="0" max="100" value={f.max_pauses} onChange={(e) => set("max_pauses", parseInt(e.target.value, 10) || 0)} className="w-24" data-testid="lesson-max-pauses" />
        </Opt>
      </div>

      <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-slate-200 rounded-xl px-4 py-3 flex justify-end gap-2">
        <Button variant="outline" onClick={back}>Cancelar</Button>
        <Button onClick={save} disabled={saving} className="text-white px-8" style={{ background: "#1E9E2A" }} data-testid="lesson-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {lid ? "Salvar aula" : "Criar aula"}</>}
        </Button>
      </div>
    </div>
  );
}
